import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Plus,
  DotsThree,
  Trash,
  PencilSimple,
  UsersThree,
  SpinnerGap,
  Crown,
  UserCircle,
  TrendUp,
  TrendDown,
  CaretDown,
  CaretUp,
  Target,
  CurrencyInr,
  ChartBar,
  Users,
  Warning,
  Trophy,
  DownloadSimple,
  X,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { FilterPanel, FilterField } from '../../components/FilterPanel';
import { ExportPreviewModal } from '../../components/ExportPreviewModal';
import { fmtINR, fmtUSD } from '../../utils/currency';
import { getTeamsUnderManager } from '../../utils/teamHierarchy';
import { sortUsersByName } from '../../utils/users';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatCurrency = (amount) => fmtINR(amount);

const TeamManagementPage = () => {
  const navigate = useNavigate();
  const { user: currentUser, isSuperadmin, isCeo, isPeopleAdmin } = useAuth();
  const isFullAdmin = isSuperadmin || isCeo;
  const canSeeAllIncentives = isFullAdmin;
  const [analytics, setAnalytics] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [teamFilter, setTeamFilter] = useState('all');
  const [deptFilter, setDeptFilter] = useState('all');
  const [expandedTeams, setExpandedTeams] = useState({});
  const [showExport, setShowExport] = useState(false);
  const [orgFilter, setOrgFilter] = useState('all');

  // Date range filter state — defaults to Year-to-Date so all imported placements are visible
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), 0, 1).toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [showTargetModal, setShowTargetModal] = useState(false);
  const [targetTeam, setTargetTeam] = useState(null);
  const [targetForm, setTargetForm] = useState({ team_given_target: '' });
  const [savingTarget, setSavingTarget] = useState(false);
  const [monthlyData, setMonthlyData] = useState(null);

  // Placement details popup state
  const [showMemberPlacementModal, setShowMemberPlacementModal] = useState(false);
  const [memberPlacementData, setMemberPlacementData] = useState([]);
  const [memberPlacementUser, setMemberPlacementUser] = useState(null);
  const [memberPlacementType, setMemberPlacementType] = useState('placements');
  const [memberPlacementLoading, setMemberPlacementLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '', department: 'sales', lead_id: '', manager_id: '', assistant_manager_id: '', member_ids: [], sub_teams: [], budget: 0, reporting_managers: [], onsite_managers: [],
  });
  const [memberSearch, setMemberSearch] = useState('');
  const [rmSearch, setRmSearch] = useState('');

  useEffect(() => { loadPageData(); }, []);

  const loadPageData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/teams-dashboard?date_from=${dateFrom}&date_to=${dateTo}`,
        { withCredentials: true },
      );
      setAnalytics(res.data.analytics || []);
      setMonthlyData(res.data.monthly || null);
    } catch (err) {
      console.error('Teams dashboard failed, falling back to separate APIs:', err);
      try {
        const [analyticsRes, monthlyRes] = await Promise.all([
          axios.get(`${API_URL}/api/admin/team-analytics?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
          axios.get(`${API_URL}/api/team-monthly-data?date_from=${dateFrom}&date_to=${dateTo}`, { withCredentials: true }),
        ]);
        setAnalytics(analyticsRes.data || []);
        setMonthlyData(monthlyRes.data || null);
      } catch (fallbackErr) {
        console.error('Failed to load teams data:', fallbackErr);
        setAnalytics([]);
        setMonthlyData(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsersForModal = async () => {
    if (users.length > 0 || usersLoading) return;
    setUsersLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/teams/users/available`, { withCredentials: true });
      setUsers(sortUsersByName(res.data));
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setUsersLoading(false);
    }
  };

  const openMemberPlacementPopup = async (member, type = 'placements') => {
    setMemberPlacementUser(member);
    setMemberPlacementType(type);
    setMemberPlacementLoading(true);
    setShowMemberPlacementModal(true);
    setMemberPlacementData([]);
    try {
      const res = await axios.get(`${API_URL}/api/admin/user/${member.id}/placement-details?type=${type}`, { withCredentials: true });
      setMemberPlacementData(res.data || []);
    } catch (e) {
      console.error('Failed to fetch placement details:', e);
    } finally {
      setMemberPlacementLoading(false);
    }
  };

  const handleOpenModal = async (team = null) => {
    await loadUsersForModal();
    if (team) {
      setEditTeam(team);
      setFormData({
        name: team.name || '', department: team.department || 'sales',
        lead_id: team.lead_id || '', manager_id: team.manager_id || '',
        assistant_manager_id: team.assistant_manager_id || '',
        member_ids: team.member_ids || [], sub_teams: team.sub_teams || [],
        budget: team.budget ?? team.team_budget ?? 0,
        reporting_managers: team.reporting_managers || [],
        onsite_managers: team.onsite_managers || [],
      });
    } else {
      setEditTeam(null);
      setFormData({ name: '', department: 'sales', lead_id: '', manager_id: '', assistant_manager_id: '', member_ids: [], sub_teams: [], budget: 0, reporting_managers: [], onsite_managers: [] });
    }
    setError(''); setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      if (editTeam) {
        const payload = isPeopleAdmin
          ? { member_ids: formData.member_ids }
          : { ...formData, budget: parseFloat(formData.budget) || 0 };
        await axios.put(`${API_URL}/api/teams/${editTeam.id}`, payload, { withCredentials: true });
      } else {
        const payload = { ...formData, budget: parseFloat(formData.budget) || 0 };
        await axios.post(`${API_URL}/api/teams`, payload, { withCredentials: true });
      }
      setShowModal(false); loadPageData();
    } catch (err) { setError(getApiErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    try { await axios.delete(`${API_URL}/api/teams/${teamId}`, { withCredentials: true }); loadPageData(); }
    catch (err) { alert('Failed to delete: ' + (err.response?.data?.detail || err.message)); }
  };

  const openTargetModal = (team) => {
    setTargetTeam(team);
    setTargetForm({
      team_given_target: team.team_given_target || '',
    });
    setShowTargetModal(true);
  };

  const handleSaveTargets = async () => {
    if (!targetTeam) return;
    setSavingTarget(true);
    try {
      const fromDate = new Date(dateFrom);
      await axios.post(`${API_URL}/api/admin/targets`, {
        team_name: targetTeam.name,
        month: fromDate.getMonth() + 1,
        year: fromDate.getFullYear(),
        target_placements: parseInt(targetForm.team_given_target) || 0,
      }, { withCredentials: true });
      setShowTargetModal(false);
      loadPageData();
    } catch (err) { alert('Failed to save targets: ' + (err.response?.data?.detail || err.message)); }
    finally { setSavingTarget(false); }
  };

  const toggleMember = (userId) => {
    setFormData((prev) => ({
      ...prev,
      member_ids: prev.member_ids.includes(userId) ? prev.member_ids.filter(id => id !== userId) : [...prev.member_ids, userId],
    }));
  };

  const toggleExpand = (teamId) => {
    setExpandedTeams(prev => ({ ...prev, [teamId]: !prev[teamId] }));
  };

  // All-active-users dropdowns — show every user in the portal regardless of role.
  // Lead and Manager may be the same person; Assistant Manager must be someone else.
  const activeUsers = () => sortUsersByName(users.filter(u => u.is_active !== false));
  const getLeadOptions = () => {
    const excl = new Set([formData.assistant_manager_id].filter(Boolean));
    return activeUsers().filter(u => !excl.has(u.id));
  };
  const getManagerOptions = () => {
    const excl = new Set([formData.assistant_manager_id].filter(Boolean));
    return activeUsers().filter(u => !excl.has(u.id));
  };
  const getAssistantManagerOptions = () => {
    const excl = new Set([formData.lead_id, formData.manager_id].filter(Boolean));
    return activeUsers().filter(u => !excl.has(u.id));
  };
  const getMemberOptions = () => {
    const excl = new Set([formData.lead_id, formData.manager_id, formData.assistant_manager_id].filter(Boolean));
    return activeUsers().filter(u => !excl.has(u.id) || (formData.member_ids || []).includes(u.id));
  };

  const getReportingManagerOptions = () => activeUsers();

  const getOnsiteManagerOptions = () => activeUsers();

  const toggleReportingManager = (userId) => {
    setFormData(prev => ({
      ...prev,
      reporting_managers: prev.reporting_managers.includes(userId)
        ? prev.reporting_managers.filter(id => id !== userId)
        : [...prev.reporting_managers, userId],
    }));
  };

  const toggleOnsiteManager = (userId) => {
    setFormData(prev => ({
      ...prev,
      onsite_managers: prev.onsite_managers.includes(userId)
        ? prev.onsite_managers.filter(id => id !== userId)
        : [...prev.onsite_managers, userId],
    }));
  };

  // Group analytics by manager
  const groupByOrganization = (data) => {
    const groups = {};
    for (const team of data) {
      const orgKey = team.organization || 'Unassigned';
      if (!groups[orgKey]) groups[orgKey] = { organization: orgKey, managers: {} };
      const mgrKey = team.manager_name || 'Unassigned';
      if (!groups[orgKey].managers[mgrKey]) groups[orgKey].managers[mgrKey] = { manager_name: mgrKey, manager_id: team.manager_id || '', teams: [] };
      groups[orgKey].managers[mgrKey].teams.push(team);
    }
    // Convert to array format
    return Object.values(groups).map(org => ({
      ...org,
      managers: Object.values(org.managers),
      teams: Object.values(org.managers).flatMap(m => m.teams),
    }));
  };

  // Filtering
  let filteredAnalytics = analytics;
  // Hide "Direct" teams (e.g., "Shoolin Direct", "Novozen Direct")
  filteredAnalytics = filteredAnalytics.filter(t => !t.name?.toLowerCase().includes('direct'));
  if (orgFilter !== 'all') filteredAnalytics = filteredAnalytics.filter(t => (t.organization || 'Unassigned') === orgFilter);
  if (deptFilter !== 'all') filteredAnalytics = filteredAnalytics.filter(t => t.department === deptFilter);
  if (teamFilter !== 'all') filteredAnalytics = filteredAnalytics.filter(t => t.name === teamFilter);

  const orgGroups = groupByOrganization(filteredAnalytics);

  // Summary stats
  const totalBudget = filteredAnalytics.reduce((s, t) => s + (t.team_budget || 0), 0);
  const totalSpending = (() => {
    const base = filteredAnalytics.reduce((s, t) => s + (t.team_spending || 0), 0);
    const seenMgrs = new Set();
    let mgrSalary = 0;
    filteredAnalytics.forEach(t => {
      if (t.manager_id && !seenMgrs.has(t.manager_id)) {
        seenMgrs.add(t.manager_id);
        mgrSalary += (t.manager_salary || 0);
      }
    });
    return base + mgrSalary;
  })();
  const totalIncentive = filteredAnalytics.reduce((s, t) => s + t.total_incentive, 0);
  const totalPlacements = filteredAnalytics.reduce((s, t) => s + t.total_placements, 0);
  const totalBackouts = filteredAnalytics.reduce((s, t) => s + t.total_backouts, 0);
  const totalCompanyTarget = filteredAnalytics.reduce((s, t) => s + (t.company_target || 0), 0) * 3;
  const totalReached = filteredAnalytics.reduce((s, t) => s + (t.reached_target || 0), 0);
  const totalSavedMargin = filteredAnalytics.reduce((s, t) => s + (t.total_saved_margin || 0), 0);

  const getRoleLabel = (role) => {
    const map = { sales_recruiter: 'Recruiter', sales_technical_recruiter: 'Recruiter', sales_lead: 'Lead', sales_manager: 'Manager', sales_assistant_manager: 'Asst. Mgr', technical_recruiter: 'Recruiter', recruiting_lead: 'Lead', recruiting_manager: 'Manager', recruiting_assistant_manager: 'Asst. Mgr', superadmin: 'Admin', ceo: 'CEO' };
    return map[role] || role?.replace(/_/g, ' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit'] tracking-tight">Teams & Analytics</h1>
          <p className="text-slate-500 mt-1">Team hierarchy, budget tracking, incentive performance, and target progress. Expand teams to view sub-teams and individual members.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowExport(true)} className="gap-1.5" data-testid="export-analytics-btn">
            <DownloadSimple size={16} /> Export to Excel
          </Button>
          {isFullAdmin && (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => handleOpenModal()} data-testid="create-team-btn">
            <Plus size={18} className="mr-2" /> Create Team
          </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Teams', val: filteredAnalytics.length, color: 'text-slate-800', icon: <UsersThree size={18} className="text-slate-500" /> },
          { label: 'Margin', val: fmtUSD(totalSavedMargin), color: 'text-teal-700', icon: <TrendUp size={18} className="text-teal-500" />, sub: fmtINR(Math.round(totalSavedMargin * 83.5)) },
          { label: 'Total Incentives', val: formatCurrency(totalIncentive), color: 'text-emerald-700', icon: <TrendUp size={18} className="text-emerald-500" />, sub: fmtUSD(Math.round(totalIncentive / 83.5)) },
          { label: 'Placements', val: totalPlacements, color: 'text-blue-700', icon: <ChartBar size={18} className="text-blue-500" /> },
          { label: 'Company Target', val: `${totalReached} / ${totalCompanyTarget}`, color: totalReached >= totalCompanyTarget ? 'text-emerald-700' : 'text-red-700', icon: <Target size={18} className={totalReached >= totalCompanyTarget ? 'text-emerald-500' : 'text-red-500'} /> },
          { label: 'Backouts', val: totalBackouts, color: 'text-red-700', icon: <Warning size={18} className="text-red-500" /> },
        ].map((stat) => (
          <Card key={stat.label} className="border-slate-200 hover:shadow-md transition-shadow" data-testid={`summary-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
            <CardContent className="p-3">
              <div className="flex items-center gap-2 mb-1">{stat.icon}<span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">{stat.label}</span></div>
              <p className={`text-lg font-bold ${stat.color}`}>{stat.val}</p>
              {stat.sub && <p className="text-[10px] text-slate-400 mt-0.5">{stat.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Company Target Formula Info */}
      <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-yellow-50" data-testid="company-target-formula-card">
        <CardContent className="p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Target size={22} className="text-amber-600" />
            <div>
              <h4 className="text-sm font-semibold text-amber-900">Company Target Formula</h4>
              <p className="text-xs text-amber-700 mt-0.5">
                <code className="bg-amber-100 px-2 py-0.5 rounded font-mono text-amber-800">Company Target = floor(Team Size x 0.75)</code>
                <span className="ml-2 text-amber-600">(Min: 1 placement per team)</span>
              </p>
            </div>
          </div>
          <div className="text-xs text-amber-600">
            Team size includes leads, assistant managers, and recruiters (excludes manager). Each team target is 75% of its total headcount.
          </div>
        </CardContent>
      </Card>


      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-4">
          <Label className="text-sm font-medium whitespace-nowrap">Filters:</Label>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-44" data-testid="filter-department"><SelectValue placeholder="Department" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              <SelectItem value="sales">Sales</SelectItem>
              <SelectItem value="recruiting">Recruiting</SelectItem>
            </SelectContent>
          </Select>
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="w-56" data-testid="filter-team-analytics"><SelectValue placeholder="All Teams" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Teams</SelectItem>
              {analytics.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-56" data-testid="filter-org"><SelectValue placeholder="All Organizations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Organizations</SelectItem>
              {[...new Set(analytics.map(t => t.organization || 'Unassigned'))].sort().map(org => (
                <SelectItem key={org} value={org}>{org}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(deptFilter !== 'all' || teamFilter !== 'all' || orgFilter !== 'all') && (
            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => { setDeptFilter('all'); setTeamFilter('all'); setOrgFilter('all'); }}>Clear filters</Button>
          )}
          <div className="ml-auto flex items-center gap-3">
            <Badge className="bg-blue-100 text-blue-700 text-xs px-3 py-1" data-testid="current-quarter">
              {(() => { const d = new Date(); return `Q${Math.floor(d.getMonth() / 3) + 1}-${d.getFullYear()}`; })()}
            </Badge>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] h-8 text-xs" data-testid="date-from-input" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 whitespace-nowrap">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] h-8 text-xs" data-testid="date-to-input" />
            </div>
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white px-4 h-8" onClick={loadPageData} data-testid="search-btn">
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Organization → Manager → Team Hierarchy */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>
      ) : orgGroups.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500">No teams found</CardContent></Card>
      ) : (
        <div className="space-y-8">
          {orgGroups.map((org) => (
            <div key={org.organization} className="space-y-4" data-testid={`org-group-${org.organization}`}>
              {/* Organization Header */}
              <div className="flex items-center justify-between bg-gradient-to-r from-blue-950 to-indigo-900 text-white px-6 py-4 rounded-xl shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <UsersThree size={22} className="text-blue-300" />
                  </div>
                  <div>
                    <p className="font-bold text-base tracking-wide cursor-pointer hover:text-blue-300 transition-colors"
                       onClick={() => navigate(`/dashboard/org/${encodeURIComponent(org.organization)}`)}
                       data-testid={`org-name-${org.organization}`}
                    >{org.organization}</p>
                    <p className="text-[10px] text-blue-300/70 uppercase tracking-widest">{org.managers.length} manager{org.managers.length !== 1 ? 's' : ''} — {org.teams.length} team{org.teams.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex gap-5 text-xs">
                  {[
                    { label: 'Company Target', value: org.teams.reduce((s, t) => s + (t.company_target || 0), 0) * 3 },
                    { label: 'Reached', value: org.teams.reduce((s, t) => s + (t.reached_target || 0), 0) },
                  ].map(m => (
                    <div key={m.label} className="text-center min-w-[70px]">
                      <p className="text-[9px] text-blue-300/60 uppercase tracking-wider mb-0.5">{m.label}</p>
                      <p className="font-bold text-white text-sm">{m.value}</p>
                    </div>
                  ))}
                  <div className="border-l border-blue-700 pl-5 text-center min-w-[80px]">
                    <p className="text-[9px] text-blue-300/60 uppercase tracking-wider mb-0.5">Incentives</p>
                    <p className="font-bold text-emerald-300 text-sm">{formatCurrency(org.teams.reduce((s, t) => s + (t.total_incentive || 0), 0))}</p>
                  </div>
                </div>
              </div>

              {/* Managers within this org */}
              {org.managers.map((mgrGroup) => {
                const leadTeams = getTeamsUnderManager(mgrGroup);
                return (
                <Card key={mgrGroup.manager_name} className="border-slate-200 overflow-hidden ml-4" data-testid={`manager-group-${mgrGroup.manager_name}`}>
                  {/* Manager Header */}
                  <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                        <Crown size={16} className="text-amber-400" />
                      </div>
                      <div>
                        <p className="font-bold text-sm tracking-wide cursor-pointer hover:text-amber-300 transition-colors"
                           onClick={() => navigate(`/dashboard/manager/${mgrGroup.manager_id}`)}
                           data-testid={`manager-name-${mgrGroup.manager_id}`}
                        >{mgrGroup.manager_name}</p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-widest">Manager — {leadTeams.length} team{leadTeams.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="flex gap-5 text-xs">
                      {[
                        { label: 'Company Target', value: leadTeams.reduce((s, t) => s + (t.company_target || 0), 0) * 3 },
                        { label: 'Team Target', value: (() => { const tt = leadTeams.reduce((s, t) => { const mt = (monthlyData?.teams || []).find(x => x.id === t.id); return s + (mt?.team_given_target || t.team_given_target || 0); }, 0); return tt > 0 ? tt * 3 : null; })() },
                        { label: 'Reached', value: leadTeams.reduce((s, t) => s + (t.reached_target || 0), 0) },
                      ].map(m => (
                        <div key={m.label} className="text-center min-w-[70px]">
                          <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">{m.label}</p>
                          <p className="font-bold text-white text-sm">{m.value !== null ? m.value : <span className="text-slate-500 text-[10px] font-normal">Not set</span>}</p>
                        </div>
                      ))}
                      <div className="border-l border-slate-600 pl-5 text-center min-w-[80px]">
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">Incentives</p>
                        <p className="font-bold text-emerald-300 text-sm">{formatCurrency(leadTeams.reduce((s, t) => s + (t.total_incentive || 0), 0))}</p>
                      </div>
                      <div className="text-center min-w-[80px]">
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider mb-0.5">Spending</p>
                        <p className="font-bold text-orange-300 text-sm">{formatCurrency(leadTeams.reduce((s, t) => s + (t.team_spending || 0), 0) + (mgrGroup.teams[0]?.manager_salary || 0))}</p>
                      </div>
                    </div>
                  </div>

                  {/* Manager Performance Card */}
                  {(() => {
                    const ms = mgrGroup.teams.find(t => t.manager_stats)?.manager_stats;
                    if (!ms) return null;
                    return (
                      <div className="px-6 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 flex flex-wrap items-center gap-6" data-testid={`mgr-perf-${ms.id}`}>
                        <div className="flex items-center gap-2">
                          <Crown size={14} className="text-amber-600" />
                          <span className="text-xs font-semibold text-amber-900 uppercase tracking-wider">Manager Performance</span>
                        </div>
                        {[
                          { label: 'Placements Created', value: ms.placements_created, color: 'text-slate-800' },
                          { label: 'Approved', value: ms.approved_created, color: 'text-emerald-700' },
                          { label: 'Incentive Share', value: `${formatCurrency(ms.incentive_share)} (${fmtUSD(Math.round((ms.incentive_share || 0) / 83.5))})`, color: 'text-blue-700' },
                          { label: 'Salary', value: formatCurrency(ms.salary), color: 'text-slate-600' },
                        ].map(s => (
                          <div key={s.label} className="text-center">
                            <p className="text-[9px] text-amber-700/60 uppercase tracking-wider">{s.label}</p>
                            <p className={`text-sm font-bold ${s.color}`}>{s.value}</p>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Teams under this manager (lead teams only — hide manager self-duplicate row) */}
                  <div className="divide-y divide-slate-100">
                    {leadTeams.map((team) => {
                      const isExpanded = expandedTeams[team.id];
                      // Quarterly targets = monthly × 3
                      const qCompanyTarget = (team.company_target || 0) * 3;
                      const targetPct = qCompanyTarget > 0 ? Math.round((team.reached_target / qCompanyTarget) * 100) : 0;

                      // Merge monthly data if available
                      const mTeam = (monthlyData?.teams || []).find(t => t.id === team.id) || {};
                      const monthlyTeamTarget = mTeam.team_given_target || team.team_given_target || 0;
                      const qTeamTarget = monthlyTeamTarget * 3;
                      const qtrApproved = team.reached_target || mTeam.month_approved || 0;
                      const quarterApproved = mTeam.quarter_approved || qtrApproved;
                      const quarterTarget = mTeam.quarter_target || qTeamTarget;
                      const bonusEligible = mTeam.bonus_eligible || false;
                      const bonusPct = mTeam.bonus_achievement_pct || 0;

                      // Group members by sub_team
                      const subTeamMembers = {};
                      const noSubTeam = [];
                      (team.members || []).forEach(m => {
                        if (m.sub_team && team.sub_teams?.includes(m.sub_team)) {
                          if (!subTeamMembers[m.sub_team]) subTeamMembers[m.sub_team] = [];
                          subTeamMembers[m.sub_team].push(m);
                        } else {
                          noSubTeam.push(m);
                        }
                      });

                      return (
                        <div key={team.id} data-testid={`team-row-${team.id}`}>
                          {/* Team Row */}
                          <div className="px-5 py-3 hover:bg-slate-50 cursor-pointer transition-colors flex items-center gap-4" onClick={() => toggleExpand(team.id)}>
                            <button className="text-slate-400 hover:text-slate-700 shrink-0">
                              {isExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-slate-900">{team.name}</span>
                                <Badge className={`text-[10px] ${team.department === 'sales' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>{team.department}</Badge>
                                {(team.sub_teams || []).length > 0 && <Badge variant="outline" className="text-[10px] text-slate-500">{team.sub_teams.length} sub-team{team.sub_teams.length !== 1 ? 's' : ''}</Badge>}
                              </div>
                              <div className="flex flex-wrap gap-4 text-[11px] text-slate-500 mt-0.5">
                                {team.lead_name && <span>Lead: <strong className="text-slate-700">{team.lead_name}</strong></span>}
                                {team.assistant_manager_name && <span>Asst. Mgr: <strong className="text-slate-700">{team.assistant_manager_name}</strong></span>}
                                {(team.reporting_manager_names || []).length > 0 && <span>Reporting Mgrs: <strong className="text-slate-700">{team.reporting_manager_names.join(', ')}</strong></span>}
                                {(team.onsite_manager_names || []).length > 0 && <span>Onsite Mgrs: <strong className="text-slate-700">{team.onsite_manager_names.join(', ')}</strong></span>}
                                <span>{team.member_count} members</span>
                              </div>
                            </div>
                            {/* Metrics inline */}
                            <div className="hidden lg:flex items-center gap-3 text-xs shrink-0">
                              <div className="text-center px-2 py-1 bg-teal-50 rounded-lg">
                                <p className="text-[9px] text-teal-400 uppercase font-medium">Margin</p>
                                <p className="font-bold text-teal-700">{fmtUSD(team.total_saved_margin || 0)}</p>
                                <p className="text-[9px] text-teal-400">{fmtINR(Math.round((team.total_saved_margin || 0) * 83.5))}</p>
                              </div>
                              <div className="text-center px-2 py-1 bg-emerald-50 rounded-lg">
                                <p className="text-[9px] text-emerald-400 uppercase font-medium">Incentive</p>
                                <p className="font-bold text-emerald-700">{formatCurrency(team.total_incentive)}</p>
                                <p className="text-[9px] text-emerald-400">{fmtUSD(Math.round((team.total_incentive || 0) / 83.5))}</p>
                              </div>
                              <div className="text-center px-2 py-1 bg-orange-50 rounded-lg">
                                <p className="text-[9px] text-orange-400 uppercase font-medium">Spending</p>
                                <p className="font-bold text-orange-700">{formatCurrency(team.team_spending || 0)}</p>
                                <p className="text-[9px] text-orange-400">{fmtUSD(Math.round((team.team_spending || 0) / 83.5))}</p>
                              </div>
                              <div className="border-l border-slate-200 pl-3 text-center">
                                <p className="text-[9px] text-slate-400 uppercase font-medium">Company Target</p>
                                <p className={`font-bold ${qCompanyTarget > 0 && qtrApproved >= qCompanyTarget ? 'text-emerald-700' : 'text-slate-700'}`}>
                                  {qCompanyTarget > 0 ? `${qtrApproved}/${qCompanyTarget}` : <span className="text-slate-400 text-[10px] font-normal">—</span>}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-[9px] text-slate-400 uppercase font-medium">Team Target</p>
                                <p className={`font-bold ${qTeamTarget > 0 && qtrApproved >= qTeamTarget ? 'text-emerald-700' : 'text-slate-700'}`}>
                                  {qTeamTarget > 0 ? `${qtrApproved}/${qTeamTarget}` : <span className="text-slate-400 text-[10px] font-normal">—</span>}
                                </p>
                              </div>
                              <div className="text-center">
                                <p className="text-[9px] text-slate-400 uppercase font-medium">Backouts</p>
                                <p className={`font-bold ${team.total_backouts > 0 ? 'text-red-600' : 'text-slate-400'}`}>{team.total_backouts}</p>
                              </div>
                              <div className="text-center">
                                <p className="text-[9px] text-slate-400 uppercase font-medium">Q-Bonus</p>
                                {bonusEligible ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">Eligible</Badge>
                                ) : quarterTarget > 0 ? (
                                  <span className="text-[10px] text-amber-600 font-semibold">{bonusPct}%</span>
                                ) : (
                                  <span className="text-[10px] text-slate-400">—</span>
                                )}
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0"><DotsThree size={18} weight="bold" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {isFullAdmin && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openTargetModal({ ...team, team_given_target: monthlyTeamTarget }); }}>
                                  <Target size={16} className="mr-2" /> Set Targets
                                </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenModal(team); }}>
                                  <PencilSimple size={16} className="mr-2" /> {isPeopleAdmin ? 'Edit Members' : 'Edit Team'}
                                </DropdownMenuItem>
                                {isFullAdmin && (
                                <DropdownMenuItem className="text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(team.id); }}>
                                  <Trash size={16} className="mr-2" /> Delete Team
                                </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {/* Mobile metrics */}
                          {isExpanded && (
                            <div className="lg:hidden px-5 pb-3 grid grid-cols-3 gap-3">
                              {[
                                { l: 'Company Target', v: `${team.reached_target}/${qCompanyTarget}`, c: targetPct >= 100 ? 'text-emerald-700' : 'text-slate-700' },
                                { l: 'Team Target', v: qTeamTarget > 0 ? `${qtrApproved}/${qTeamTarget}` : '—', c: 'text-slate-700' },
                                { l: 'Margin', v: fmtUSD(team.total_saved_margin || 0), c: 'text-teal-700', s: fmtINR(Math.round((team.total_saved_margin || 0) * 83.5)) },
                                { l: 'Incentive', v: formatCurrency(team.total_incentive), c: 'text-emerald-700', s: fmtUSD(Math.round((team.total_incentive || 0) / 83.5)) },
                              ].map(m => (
                                <div key={m.l} className="text-center bg-slate-50 rounded-lg p-2.5 border border-slate-100">
                                  <p className="text-[9px] text-slate-400 uppercase font-medium">{m.l}</p>
                                  <p className={`text-sm font-bold ${m.c}`}>{m.v}</p>
                                  {m.s && <p className="text-[9px] text-slate-400">{m.s}</p>}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Expanded: Sub-teams and Members */}
                          {isExpanded && (
                            <div className="bg-gradient-to-b from-slate-50/80 to-white border-t border-slate-100">
                              {/* Target Progress Bars + Quarterly Bonus */}
                              <div className="px-6 py-4 border-b border-slate-100 space-y-3">
                                <div className="flex items-center gap-4">
                                  <span className="text-xs text-slate-500 w-32 shrink-0 font-medium">Company Target <span className="text-[9px] text-slate-400">({team.target_headcount || team.recruiter_count || '?'} headcount)</span></span>
                                  <div className="flex-1 bg-slate-200/60 rounded-full h-3 overflow-hidden shadow-inner">
                                    <div className={`h-full rounded-full transition-all duration-500 ${targetPct >= 100 ? 'bg-gradient-to-r from-emerald-500 to-emerald-400' : targetPct >= 50 ? 'bg-gradient-to-r from-amber-500 to-amber-400' : 'bg-gradient-to-r from-red-500 to-red-400'}`} style={{ width: `${Math.min(targetPct, 100)}%` }} />
                                  </div>
                                  <span className={`text-xs font-semibold w-20 text-right ${targetPct >= 100 ? 'text-emerald-700' : 'text-slate-700'}`}>{team.reached_target}/{qCompanyTarget} ({targetPct}%)</span>
                                  <span className="text-[10px] text-slate-400 w-24 text-right">{team.target_headcount || team.recruiter_count || '?'} headcount</span>
                                </div>
                                {qTeamTarget > 0 && (
                                  <div className="flex items-center gap-4">
                                    <span className="text-xs text-slate-500 w-28 shrink-0">Team Target</span>
                                    <div className="flex-1 bg-slate-200 rounded-full h-2.5 overflow-hidden">
                                      <div className={`h-full rounded-full transition-all ${qtrApproved >= qTeamTarget ? 'bg-emerald-500' : qtrApproved >= qTeamTarget * 0.5 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(Math.round(qtrApproved / qTeamTarget * 100), 100)}%` }} />
                                    </div>
                                    <span className="text-xs font-semibold w-20 text-right">{qtrApproved}/{qTeamTarget} ({qTeamTarget > 0 ? Math.round(qtrApproved / qTeamTarget * 100) : 0}%)</span>
                                  </div>
                                )}
                                {/* Quarterly Bonus Status */}
                                <div className="flex items-center gap-3 pt-1 border-t border-slate-200 mt-2">
                                  <Trophy size={16} className="text-amber-600" />
                                  <span className="text-xs text-slate-600">
                                    Quarterly Bonus ({monthlyData?.quarter || '-'}): {quarterApproved} approved / {quarterTarget > 0 ? `${quarterTarget} target` : 'No quarterly target set'}
                                  </span>
                                  {bonusEligible ? (
                                    <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Bonus Eligible</Badge>
                                  ) : quarterTarget > 0 ? (
                                    <span className="text-xs text-amber-600 font-medium">({bonusPct}% achieved)</span>
                                  ) : null}
                                </div>
                              </div>

                              {/* Sub Teams sections */}
                              {(team.sub_teams || []).map(st => (
                                <div key={st} className="border-b border-slate-100">
                                  <div className="px-6 py-2 bg-slate-100/80 flex items-center gap-2">
                                    <Users size={14} className="text-slate-500" />
                                    <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Sub-team: {st}</span>
                                    <Badge variant="outline" className="text-[10px]">{(subTeamMembers[st] || []).length} members</Badge>
                                  </div>
                                  {(subTeamMembers[st] || []).length > 0 ? (
                                    <MemberTable members={subTeamMembers[st]} getRoleLabel={getRoleLabel} onPlacementClick={openMemberPlacementPopup} teamAutoTarget={team.company_target} teamMemberCount={(team.members || []).length} canSeeAllIncentives={canSeeAllIncentives} currentUserId={currentUser?.id} onNameClick={canSeeAllIncentives ? (id) => navigate(`/dashboard/user/${id}`) : null} quarterLabel={monthlyData?.quarter} />
                                  ) : (
                                    <p className="px-8 py-2 text-xs text-slate-400 italic">No members assigned to this sub-team</p>
                                  )}
                                </div>
                              ))}

                              {/* Members without sub-team */}
                              {noSubTeam.length > 0 && (
                                <div>
                                  {(team.sub_teams || []).length > 0 && (
                                    <div className="px-6 py-2 bg-slate-100/80">
                                      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Direct Members</span>
                                    </div>
                                  )}
                                  <MemberTable members={noSubTeam} getRoleLabel={getRoleLabel} onPlacementClick={openMemberPlacementPopup} teamAutoTarget={team.company_target} teamMemberCount={(team.members || []).length} canSeeAllIncentives={canSeeAllIncentives} currentUserId={currentUser?.id} onNameClick={canSeeAllIncentives ? (id) => navigate(`/dashboard/user/${id}`) : null} quarterLabel={monthlyData?.quarter} />
                                </div>
                              )}

                              {(team.members || []).length === 0 && (
                                <p className="px-6 py-4 text-sm text-slate-400 text-center">No members in this team</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Member Placement Details Popup */}
      <Dialog open={showMemberPlacementModal} onOpenChange={setShowMemberPlacementModal}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Outfit']">
              {memberPlacementType === 'backouts' ? 'Backout Details' : 'Placement Details'} — {memberPlacementUser?.name}
            </DialogTitle>
            <DialogDescription>
              {memberPlacementLoading ? 'Loading...' : `${memberPlacementData.length} ${memberPlacementType === 'backouts' ? 'backout(s)' : 'placement(s)'} found`}
            </DialogDescription>
          </DialogHeader>
          {memberPlacementLoading ? (
            <div className="flex items-center justify-center py-8"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>
          ) : memberPlacementData.length === 0 ? (
            <div className="py-8 text-center text-slate-400">No {memberPlacementType === 'backouts' ? 'backouts' : 'placements'} found for this user</div>
          ) : (
            <div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="member-placement-popup-table">
                  <thead className="bg-slate-50 sticky top-0 z-10">
                    <tr>
                      {['ID', 'Candidate', 'Category', 'Module', 'Status', 'Bill Rate', 'Pay Rate', 'Margin', 'Total Incentive', 'SR', 'SRC', 'SL', 'SM', 'SRCL', 'SRCM', 'Start Date'].map(h => (
                        <th key={h} className="px-2 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {memberPlacementData.map((p, idx) => (
                      <tr key={idx} className="hover:bg-slate-50" data-testid={`popup-placement-row-${idx}`}>
                        <td className="px-2 py-2">
                          <button onClick={() => { setShowMemberPlacementModal(false); navigate(`/dashboard/placements/${p.id}`); }}
                            className="text-blue-600 hover:text-blue-800 underline font-mono text-[10px]" data-testid={`popup-placement-link-${idx}`}>
                            {p.id?.slice(-6)}
                          </button>
                        </td>
                        <td className="px-2 py-2 font-medium text-slate-800 whitespace-nowrap">{p.candidate_name}</td>
                        <td className="px-2 py-2 text-slate-600">{p.category?.replace(/_/g, ' ')}</td>
                        <td className="px-2 py-2"><Badge variant="outline" className="text-[9px]">{p.module}</Badge></td>
                        <td className="px-2 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${p.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : p.status === 'backed_out' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {p.status?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">${p.bill_rate?.toFixed(2)}</td>
                        <td className="px-2 py-2 whitespace-nowrap">${p.pay_rate?.toFixed(2)}</td>
                        <td className="px-2 py-2 whitespace-nowrap font-medium">${p.margin?.toFixed(2)}</td>
                        <td className="px-2 py-2 whitespace-nowrap font-bold text-emerald-700">{fmtINR(p.total_placement)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmtINR(p.sales_recruiter)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmtINR(p.sourcing_recruiter)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmtINR(p.sales_lead)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmtINR(p.sales_manager)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmtINR(p.sourcing_lead)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-600">{fmtINR(p.sourcing_manager)}</td>
                        <td className="px-2 py-2 whitespace-nowrap text-slate-500">{p.start_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Summary Footer */}
              <div className="mt-3 p-3 bg-slate-50 rounded-lg flex items-center gap-6 text-sm">
                <span className="font-semibold text-slate-700">
                  Total Incentive: <span className="text-emerald-700">{fmtINR(memberPlacementData.reduce((s, p) => s + (p.total_placement || 0), 0))}</span>
                </span>
                <span className="text-slate-500">
                  Avg Margin: <span className="font-medium text-slate-700">${memberPlacementData.length > 0 ? (memberPlacementData.reduce((s, p) => s + (p.margin || 0), 0) / memberPlacementData.length).toFixed(2) : '0.00'}/hr</span>
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Team Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col gap-0 p-0 overflow-hidden">
          <div className="shrink-0 bg-white border-b border-slate-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Outfit'] font-bold">{editTeam ? (isPeopleAdmin ? 'Edit Team Members' : 'Edit Team') : 'Create New Team'}</DialogTitle>
              <DialogDescription>{editTeam ? (isPeopleAdmin ? 'Add or remove members from this team' : 'Update team details, hierarchy, and members') : 'Set up a new team with hierarchy and members'}</DialogDescription>
            </DialogHeader>
          </div>
          <form onSubmit={handleSave} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

            {isPeopleAdmin && editTeam && (
              <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg text-sm text-blue-900">
                Managing members for <strong>{editTeam.name}</strong>
              </div>
            )}

            {/* Row 1: Name + Department */}
            {!isPeopleAdmin && (
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Team Name *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g. Team Sunil" required data-testid="input-team-name" className="h-10" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Department *</Label>
                <Select value={formData.department} onValueChange={(v) => setFormData({...formData, department: v, lead_id: '', manager_id: '', assistant_manager_id: '', member_ids: []})}>
                  <SelectTrigger data-testid="select-department" className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="recruiting">Recruiting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            )}

            {/* Row 2: Lead + Manager */}
            {!isPeopleAdmin && (
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Lead</Label>
                <Select value={formData.lead_id || 'none'} onValueChange={(v) => setFormData({...formData, lead_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="select-lead" className="h-10"><SelectValue placeholder="Select lead..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No lead assigned</SelectItem>
                    {getLeadOptions().map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-slate-400 ml-1">({u.email})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Manager</Label>
                <Select value={formData.manager_id || 'none'} onValueChange={(v) => setFormData({...formData, manager_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="select-manager" className="h-10"><SelectValue placeholder="Select manager..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No manager assigned</SelectItem>
                    {getManagerOptions().map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-slate-400 ml-1">({u.email})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            )}

            {/* Row 3: Asst Manager + Budget */}
            {!isPeopleAdmin && (
            <div className="grid grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Assistant Manager</Label>
                <Select value={formData.assistant_manager_id || 'none'} onValueChange={(v) => setFormData({...formData, assistant_manager_id: v === 'none' ? '' : v})}>
                  <SelectTrigger data-testid="select-assistant-manager" className="h-10"><SelectValue placeholder="Select assistant manager..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No assistant manager</SelectItem>
                    {getAssistantManagerOptions().map(u => <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-slate-400 ml-1">({u.email})</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Monthly Budget</Label>
                <Input type="number" value={formData.budget} onChange={(e) => setFormData({...formData, budget: e.target.value})} placeholder="0" data-testid="input-team-budget" className="h-10" />
              </div>
            </div>
            )}

            {/* Team Members — Chip-based with search */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Team Members</Label>
                <span className="text-xs text-slate-400">{formData.member_ids.length} member(s)</span>
              </div>
              {/* Selected members as chips */}
              {formData.member_ids.length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="selected-members-chips">
                  {formData.member_ids.map(uid => {
                    const u = users.find(x => x.id === uid);
                    if (!u) return null;
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-blue-600 text-white text-xs font-medium rounded-full shadow-sm hover:bg-blue-700 transition-colors" data-testid={`member-chip-${uid}`}>
                        {u.name}
                        <button type="button" onClick={() => toggleMember(uid)} className="ml-0.5 p-0.5 rounded-full hover:bg-blue-800 transition-colors" data-testid={`remove-member-${uid}`}>
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              {/* Search to add members */}
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                  placeholder="Search by name or email to add members..."
                  className="pl-9 h-9 text-sm"
                  data-testid="member-search-input"
                />
              </div>
              {/* Filtered member list */}
              {memberSearch.trim() && (
                <div className="border border-slate-200 rounded-lg max-h-44 overflow-y-auto shadow-sm" data-testid="member-search-results">
                  {(() => {
                    const q = memberSearch.toLowerCase();
                    const results = getMemberOptions().filter(u =>
                      !formData.member_ids.includes(u.id) &&
                      (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
                    );
                    if (results.length === 0) return <p className="p-3 text-sm text-slate-400 text-center">No matching members found</p>;
                    return results.slice(0, 15).map(u => (
                      <button
                        key={u.id} type="button"
                        onClick={() => { toggleMember(u.id); setMemberSearch(''); }}
                        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 text-left border-b border-slate-50 last:border-0 transition-colors"
                        data-testid={`add-member-${u.id}`}
                      >
                        <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {u.name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                          <p className="text-xs text-slate-400 truncate">{u.email}{u.team ? ` · ${u.team}` : ''}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] flex-shrink-0">{getRoleLabel(u.role)}</Badge>
                        <Plus size={14} className="text-blue-500 flex-shrink-0" />
                      </button>
                    ));
                  })()}
                </div>
              )}
              {/* Show all members (non-search mode) — compact checkbox list */}
              {!memberSearch.trim() && (
                <div className="border border-slate-200 rounded-lg max-h-44 overflow-y-auto">
                  {getMemberOptions().length === 0 ? (
                    <p className="p-3 text-sm text-slate-400 text-center">No available members for this department</p>
                  ) : (
                    <div className="divide-y divide-slate-50">
                      {getMemberOptions().map(u => (
                        <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors">
                          <Checkbox checked={formData.member_ids.includes(u.id)} onCheckedChange={() => toggleMember(u.id)} />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm font-medium text-slate-700">{u.name}</span>
                            <span className="text-xs text-slate-400 ml-1.5">{u.email}</span>
                          </div>
                          {u.team && <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{u.team}</span>}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Reporting Managers — Chip-based */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Reporting Managers <span className="font-normal text-slate-400">(CEO, Managers, Onsite)</span></Label>
                <span className="text-xs text-slate-400">{(formData.reporting_managers || []).length} selected</span>
              </div>
              {(formData.reporting_managers || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  {(formData.reporting_managers || []).map(uid => {
                    const u = users.find(x => x.id === uid);
                    if (!u) return null;
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-amber-600 text-white text-xs font-medium rounded-full shadow-sm hover:bg-amber-700 transition-colors">
                        {u.name}
                        <button type="button" onClick={() => toggleReportingManager(uid)} className="ml-0.5 p-0.5 rounded-full hover:bg-amber-800 transition-colors">
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={rmSearch}
                  onChange={(e) => setRmSearch(e.target.value)}
                  placeholder="Search reporting managers..."
                  className="pl-9 h-9 text-sm"
                  data-testid="rm-search-input"
                />
              </div>
              <div className="border border-slate-200 rounded-lg max-h-36 overflow-y-auto">
                {(() => {
                  const q = rmSearch.toLowerCase();
                  const opts = getReportingManagerOptions().filter(u =>
                    !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q)
                  );
                  if (opts.length === 0) return <p className="p-3 text-sm text-slate-400 text-center">No matching reporting managers</p>;
                  return opts.map(u => (
                    <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50 last:border-0">
                      <Checkbox checked={(formData.reporting_managers || []).includes(u.id)} onCheckedChange={() => toggleReportingManager(u.id)} data-testid={`rm-checkbox-${u.id}`} />
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-slate-700">{u.name}</span>
                        <span className="text-xs text-slate-400 ml-1.5">{u.email}</span>
                      </div>
                      <Badge variant="outline" className="text-[10px]">{getRoleLabel(u.role)}</Badge>
                    </label>
                  ));
                })()}
              </div>
            </div>

            {/* Onsite Managers — Chip-based */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Onsite Managers</Label>
                <span className="text-xs text-slate-400">{(formData.onsite_managers || []).length} selected</span>
              </div>
              {(formData.onsite_managers || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  {(formData.onsite_managers || []).map(uid => {
                    const u = users.find(x => x.id === uid);
                    if (!u) return null;
                    return (
                      <span key={uid} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 bg-purple-600 text-white text-xs font-medium rounded-full shadow-sm hover:bg-purple-700 transition-colors">
                        {u.name}
                        <button type="button" onClick={() => toggleOnsiteManager(uid)} className="ml-0.5 p-0.5 rounded-full hover:bg-purple-800 transition-colors">
                          <X size={12} weight="bold" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="border border-slate-200 rounded-lg max-h-32 overflow-y-auto">
                {getOnsiteManagerOptions().length === 0 ? (
                  <p className="p-3 text-sm text-slate-400 text-center">No onsite managers available</p>
                ) : (
                  getOnsiteManagerOptions().map(u => (
                    <label key={u.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer transition-colors border-b border-slate-50 last:border-0">
                      <Checkbox checked={(formData.onsite_managers || []).includes(u.id)} onCheckedChange={() => toggleOnsiteManager(u.id)} data-testid={`om-checkbox-${u.id}`} />
                      <span className="text-sm font-medium text-slate-700">{u.name}</span>
                      <span className="text-xs text-slate-400">{u.email}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* Sub Teams */}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sub Teams</Label>
              <Input
                value={(formData.sub_teams || []).join(', ')}
                onChange={(e) => setFormData({...formData, sub_teams: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                placeholder="Sub Team A, Sub Team B"
                data-testid="input-sub-teams"
                className="h-10"
              />
              <p className="text-xs text-slate-400">Comma-separated sub-team names</p>
            </div>
            </div>
            <div className="shrink-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => { setShowModal(false); setMemberSearch(''); setRmSearch(''); }}>Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 min-w-[120px]" disabled={saving} data-testid="save-team-btn">
                {saving && <SpinnerGap size={16} className="animate-spin mr-2" />} {editTeam ? 'Update Team' : 'Create Team'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Set Targets Modal */}
      <Dialog open={showTargetModal} onOpenChange={setShowTargetModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Monthly Targets</DialogTitle>
            <DialogDescription>
              Set monthly target for <strong>{targetTeam?.name}</strong> — {new Date(dateFrom).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}. Quarterly values are auto-calculated (×3).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">Company Target is auto-calculated: <code className="bg-blue-100 px-1.5 py-0.5 rounded font-mono text-blue-800">floor(Headcount x 0.75)</code></p>
            </div>
            <div className="space-y-2">
              <Label>Monthly Target (Placements)</Label>
              <Input
                type="number"
                value={targetForm.team_given_target}
                onChange={(e) => setTargetForm({ ...targetForm, team_given_target: e.target.value })}
                placeholder="e.g. 8"
                data-testid="input-team-given-target"
              />
              <p className="text-xs text-slate-400">Monthly placement target. Quarterly target = this × 3. Bonus eligibility based on quarterly target.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTargetModal(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSaveTargets} disabled={savingTarget}>
              {savingTarget && <SpinnerGap size={16} className="animate-spin mr-2" />} Save Targets
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExportPreviewModal open={showExport} onClose={() => setShowExport(false)}
        data={buildExportData(filteredAnalytics)}
        fileName={`Team_Analytics_${new Date().toISOString().slice(0,10)}`}
        title="Team Analytics Export Preview"
        columns={[
          { key: 'team', header: 'Team', accessor: r => r.team_name },
          { key: 'dept', header: 'Department', accessor: r => r.department },
          { key: 'manager', header: 'Manager', accessor: r => r.manager },
          { key: 'name', header: 'Member', accessor: r => r.name },
          { key: 'role', header: 'Role', accessor: r => r.role },
          { key: 'placements', header: 'Placements', accessor: r => r.placements },
          { key: 'approved', header: 'Approved', accessor: r => r.approved },
          { key: 'backouts', header: 'Backouts', accessor: r => r.backouts },
          { key: 'margin', header: 'Margin ($)', accessor: r => r.saved_margin || 0 },
          { key: 'salary', header: 'Salary (INR)', accessor: r => r.salary || 0 },
          { key: 'incentive', header: 'Incentive (INR)', accessor: r => r.incentive || 0 },
        ]}
      />
    </div>
  );
};

// Sub-component: Member table used inside expanded teams
const MemberTable = ({ members, getRoleLabel, onPlacementClick, teamAutoTarget, teamMemberCount, canSeeAllIncentives, currentUserId, onNameClick, quarterLabel }) => {
  const perMemberExpected = teamMemberCount > 0 ? Math.max(1, Math.ceil((teamAutoTarget * 3) / teamMemberCount)) : 1;

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="bg-blue-50/60 border-b border-blue-100">
          <th colSpan={9} className="px-6 py-1.5 text-left">
            <span className="text-[10px] font-semibold text-blue-700 uppercase tracking-wider">This Quarter Data{quarterLabel ? ` (${quarterLabel})` : ''}</span>
          </th>
        </tr>
        <tr className="text-[10px] text-slate-400 uppercase tracking-wider">
          <th className="px-6 py-1.5 text-left font-semibold">Name</th>
          <th className="px-3 py-1.5 text-left font-semibold">Role</th>
          <th className="px-3 py-1.5 text-center font-semibold">Placements</th>
          <th className="px-3 py-1.5 text-center font-semibold">Approved</th>
          <th className="px-3 py-1.5 text-center font-semibold">Effective</th>
          <th className="px-3 py-1.5 text-center font-semibold">Backouts</th>
          <th className="px-3 py-1.5 text-right font-semibold">Salary</th>
          <th className="px-3 py-1.5 text-right font-semibold">Margin</th>
          <th className="px-3 py-1.5 text-right font-semibold">Incentive</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-50">
        {members.map((m) => {
          const effectiveAppr = m.effective_approved ?? m.approved;
          const didNotReachTarget = effectiveAppr < perMemberExpected;
          const isOwnRow = m.id === currentUserId;
          const showIncentive = canSeeAllIncentives || isOwnRow;
          const hasLowEnd = (m.low_end_approved || 0) > 0;
          return (
            <tr key={m.id} className={`hover:bg-white/80 transition-colors ${didNotReachTarget ? 'bg-red-50/60' : ''}`} data-testid={`member-row-${m.id}`}>
              <td className="px-6 py-2">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${didNotReachTarget ? 'bg-red-200 text-red-800' : 'bg-slate-200 text-slate-600'}`}>{m.name?.charAt(0)}</div>
                  {onNameClick ? (
                    <button onClick={() => onNameClick(m.id)} className={`font-medium underline cursor-pointer ${didNotReachTarget ? 'text-red-800 hover:text-red-900' : 'text-blue-700 hover:text-blue-900'}`} data-testid={`member-name-${m.id}`}>{m.name}</button>
                  ) : (
                    <span className={`font-medium ${didNotReachTarget ? 'text-red-800' : 'text-slate-800'}`}>{m.name}</span>
                  )}
                  {didNotReachTarget && <span className="text-[9px] text-red-500 font-semibold ml-1 bg-red-100 px-1.5 py-0.5 rounded">BELOW TARGET</span>}
                </div>
              </td>
              <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{getRoleLabel(m.role)}</Badge></td>
              <td className="px-3 py-2 text-center">
                <button
                  onClick={() => m.placements > 0 && onPlacementClick(m, 'placements')}
                  className={`font-bold ${m.placements > 0 ? 'text-blue-700 hover:text-blue-900 underline cursor-pointer' : 'text-slate-400 cursor-default'}`}
                  data-testid={`member-placements-${m.id}`}
                >
                  {m.placements}
                </button>
              </td>
              <td className="px-3 py-2 text-center">
                <span className="font-medium text-emerald-600">{m.approved}</span>
                {hasLowEnd && <span className="text-[9px] text-amber-600 ml-1">({m.low_end_approved} LE)</span>}
              </td>
              <td className="px-3 py-2 text-center">
                <span className={`font-bold ${didNotReachTarget ? 'text-red-700' : 'text-blue-700'}`}>{effectiveAppr}</span>
                <span className="text-[9px] text-slate-400 ml-0.5">/{perMemberExpected}</span>
              </td>
              <td className="px-3 py-2 text-center">
                <button
                  onClick={() => m.backouts > 0 && onPlacementClick(m, 'backouts')}
                  className={`font-bold ${m.backouts > 0 ? 'text-red-600 hover:text-red-800 underline cursor-pointer' : 'text-slate-400 cursor-default'}`}
                  data-testid={`member-backouts-${m.id}`}
                >
                  {m.backouts}
                </button>
              </td>
              <td className="px-3 py-2 text-right">
                {showIncentive && <span className="text-xs text-slate-600">{m.salary ? fmtINR(m.salary) : '-'}</span>}
              </td>
              <td className="px-3 py-2 text-right">
                {showIncentive && <span className="text-xs font-medium text-teal-700">{m.saved_margin ? fmtUSD(m.saved_margin) : '-'}</span>}
              </td>
              <td className="px-3 py-2 text-right">
                <span className="font-semibold text-emerald-700 block">{showIncentive ? fmtINR(m.incentive) : '—'}</span>
                {showIncentive && m.incentive > 0 && <span className="text-[9px] text-slate-400">{fmtUSD(Math.round((m.incentive || 0) / 83.5))}</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Build export data from all members across analytics teams
const buildExportData = (analyticsData) => {
  const rows = [];
  for (const team of analyticsData) {
    for (const m of team.members || []) {
      rows.push({ team_name: team.name, department: team.department, manager: team.manager_name, ...m });
    }
  }
  return rows;
};

export default TeamManagementPage;
